import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_CURATION_MS } from "@/lib/realtime";
import {
  useCreateMemory,
  useDeleteMemory,
  usePreviewMemoryImport,
  useSaveMemoryImport,
  useUpdateMemory,
  type MemoryList,
} from "./memoryMutations";

export type BrandMemorySource = "manual" | "chat" | "client_feedback" | "import";

export type BrandMemory = {
  id: string;
  body: string;
  source: BrandMemorySource;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
};

export const brandMemoriesKey = (brandId: string | null) =>
  ["brand-memories", brandId] as const;

/**
 * Brand memory is Owner-only: the API answers 403 to everyone else, so callers
 * pass `enabled: isOwner` rather than firing a request that can only fail. The
 * agents still save memories from Contributor chats; they just aren't shown.
 */
export function useBrandMemories(brandId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: brandMemoriesKey(brandId),
    enabled: Boolean(brandId) && (options?.enabled ?? true),
    queryFn: async () => {
      const res = await axiosInstance.get<MemoryList<BrandMemory>>(`/brand-memories/${brandId}`);
      return res.data;
    },
    // Memories are written by SaveMemory in the agent chats and by round-close
    // distillation, both server-side from this page's point of view.
    refetchInterval: POLL_CURATION_MS,
  });
}

function target(brandId: string | null) {
  return {
    key: brandMemoriesKey(brandId),
    basePath: brandId ? `/brand-memories/${brandId}` : null,
  };
}

export function useCreateBrandMemory(brandId: string | null) {
  return useCreateMemory<BrandMemory>(target(brandId), {
    source: "manual",
    createdByUserId: null,
  });
}

export function useUpdateBrandMemory(brandId: string | null) {
  return useUpdateMemory<BrandMemory>(target(brandId));
}

export function useDeleteBrandMemory(brandId: string | null) {
  return useDeleteMemory<BrandMemory>(target(brandId));
}

export function usePreviewBrandMemoryImport(brandId: string | null) {
  return usePreviewMemoryImport(target(brandId));
}

export function useSaveBrandMemoryImport(brandId: string | null) {
  return useSaveMemoryImport<BrandMemory>(target(brandId), {
    source: "import",
    createdByUserId: null,
  });
}
